import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, ClipboardList, Home, Calendar, Star, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import SEOHelmet from "@/components/SEOHelmet";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { trackServiceView } from "@/components/GoogleAnalytics";

const resources = [
  {
    id: 'seasonal-checklist',
    title: 'Michigan Seasonal Organization Checklist',
    description: 'Complete guide for organizing your home through Michigan\'s four seasons. Includes monthly tasks and storage solutions.',
    type: 'Checklist',
    icon: ClipboardList,
    popular: true,
    preview: 'Get organized for every Michigan season with our comprehensive checklist covering winter prep, spring cleaning, summer organization, and fall storage.',
    benefits: [
      'Monthly organization tasks for each season',
      'Storage solutions for weather gear',
      'Clothing rotation systems',
      'Holiday decoration storage',
      'Basement and garage organization'
    ]
  },
  {
    id: 'small-space-guide',
    title: 'Small Space Organization Guide',
    description: 'Maximize every square foot with proven strategies for Michigan\'s smaller homes and apartments.',
    type: 'Guide',
    icon: Home,
    popular: true,
    preview: 'Transform cramped spaces into organized, functional areas with vertical storage, multi-purpose furniture, and smart systems.',
    benefits: [
      'Vertical storage solutions',
      'Multi-functional furniture ideas',
      'Room-by-room strategies',
      'Before & after examples',
      'Shopping list with recommended products'
    ]
  },
  {
    id: 'adhd-organization-kit',
    title: 'ADHD-Friendly Organization Kit',
    description: 'Visual systems and simple strategies designed specifically for neurodivergent minds.',
    type: 'Toolkit',
    icon: Star,
    popular: false,
    preview: 'Evidence-based organizing methods that work with ADHD brains, not against them. Includes visual systems and maintenance strategies.',
    benefits: [
      'Visual organization systems',
      'Color-coding templates',
      'Simple maintenance routines',
      'Body doubling strategies',
      'Printable labels and charts'
    ]
  },
  {
    id: 'real-estate-staging-checklist',
    title: 'Home Staging Success Checklist',
    description: 'Prepare your Michigan home to sell faster and for more money with professional staging secrets.',
    type: 'Checklist',
    icon: FileText,
    popular: false,
    preview: 'Room-by-room staging checklist used by professional stagers to help Michigan homes sell 30% faster.',
    benefits: [
      'Room-by-room staging steps',
      'Michigan market insights',
      'Curb appeal improvements',
      'Photography preparation tips',
      'Timeline for optimal results'
    ]
  },
  {
    id: 'decluttering-calendar',
    title: '30-Day Decluttering Calendar',
    description: '15-minute daily tasks that transform your entire home without overwhelm.',
    type: 'Calendar',
    icon: Calendar,
    popular: true,
    preview: 'Gentle, sustainable decluttering approach with just 15 minutes per day. Perfect for busy Michigan families.',
    benefits: [
      'Daily 15-minute tasks',
      'Progress tracking sheets',
      'Before & after photos guide',
      'Donation drop-off locations',
      'Motivation and celebration ideas'
    ]
  },
  {
    id: 'emergency-prep-organizer',
    title: 'Michigan Emergency Preparedness Organizer',
    description: 'Essential organization for severe weather, power outages, and Michigan\'s unique emergency situations.',
    type: 'Guide',
    icon: ClipboardList,
    popular: false,
    preview: 'Organize emergency supplies and create action plans for Michigan weather emergencies, power outages, and winter storms.',
    benefits: [
      'Emergency supply checklists',
      'Storage organization systems',
      'Important document organization',
      'Communication plans',
      'Seasonal emergency updates'
    ]
  }
];

const Resources = () => {
  const [email, setEmail] = useState('');
  const [selectedResource, setSelectedResource] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleBookingClick = () => {
    trackServiceView('Resources - Book Consultation');
    navigate('/book');
  };

  const handleDownload = async (resourceId: string) => {
    if (!email.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter your email address to download the free resource.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setSelectedResource(resourceId);

    try {
      // Simulate API call to collect email and send resource
      await new Promise(resolve => setTimeout(resolve, 1500));

      trackServiceView(`Resource Download - ${resourceId}`);
      
      toast({
        title: "Download Ready!",
        description: "Check your email - your free organizing resource is on its way!",
      });

      // In a real app, this would trigger the download or email
      setEmail('');
      
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "There was an error processing your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setSelectedResource(null);
    }
  };

  return (
    <>
      <SEOHelmet 
        title="Free Organization Resources & Guides | Michigan Home Organizing"
        description="Download free organization checklists, guides, and resources. Seasonal organization, small space solutions, ADHD-friendly systems, and home staging tips for Michigan homeowners."
        keywords="free organization resources, decluttering checklist, home organization guide, Michigan organizing tips, ADHD organization, small space solutions, home staging checklist"
        canonicalUrl="https://organizebydesignmichigan.com/resources"
      />
      
      <Header onBookingClick={handleBookingClick} />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-b from-secondary/20 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6 text-foreground">
                Free Organization Resources
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Download proven guides, checklists, and toolkits to transform your Michigan home. All resources are created by professional organizers and completely free.
              </p>
              <div className="flex items-center justify-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center">
                  <Star className="w-4 h-4 mr-1 text-accent" />
                  <span>Expert-created</span>
                </div>
                <div className="flex items-center">
                  <Download className="w-4 h-4 mr-1 text-accent" />
                  <span>Instant download</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-1 text-accent" />
                  <span>Email delivery</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Email Signup */}
        <section className="py-8 bg-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="Enter your email for instant access"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="text-lg py-3"
                  />
                </div>
                <Button 
                  onClick={handleBookingClick}
                  variant="outline"
                  size="lg"
                  className="whitespace-nowrap"
                >
                  Get Personal Help Instead
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-2 text-center">
                Enter your email once to download any resource. We'll never spam you.
              </p>
            </div>
          </div>
        </section>

        {/* Resources Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {resources.map((resource) => {
                const IconComponent = resource.icon;
                const isDownloading = selectedResource === resource.id && isSubmitting;
                
                return (
                  <Card 
                    key={resource.id} 
                    className="hover-lift relative overflow-hidden"
                  >
                    {resource.popular && (
                      <div className="absolute top-4 right-4 z-10">
                        <Badge className="bg-accent text-accent-foreground">
                          Popular
                        </Badge>
                      </div>
                    )}
                    
                    <CardHeader className="pb-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <IconComponent className="w-6 h-6 text-primary" />
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {resource.type}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl leading-tight">
                        {resource.title}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {resource.description}
                      </p>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <div className="bg-secondary/20 p-4 rounded-lg">
                        <p className="text-sm font-medium mb-2">What's included:</p>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {resource.benefits.slice(0, 3).map((benefit, index) => (
                            <li key={index} className="flex items-center">
                              <div className="w-1.5 h-1.5 bg-accent rounded-full mr-2 flex-shrink-0" />
                              {benefit}
                            </li>
                          ))}
                          {resource.benefits.length > 3 && (
                            <li className="text-xs text-muted-foreground/70">
                              + {resource.benefits.length - 3} more items
                            </li>
                          )}
                        </ul>
                      </div>
                      
                      <Button 
                        onClick={() => handleDownload(resource.id)}
                        disabled={isDownloading || !email.trim()}
                        className="w-full brand-gradient hover:opacity-90 text-white font-semibold"
                      >
                        {isDownloading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download Free
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-16 bg-secondary/10">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-serif font-bold mb-4">
                Want More Organization Tips?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Get weekly organizing tips, seasonal guides, and exclusive Michigan-specific advice delivered to your inbox.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <Input
                  type="email"
                  placeholder="Your email address"
                  className="flex-1"
                />
                <Button className="luxury-gradient hover:opacity-90 text-white font-semibold">
                  Subscribe
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                Join 500+ Michigan homeowners getting organized. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </section>

        {/* Personal Help CTA */}
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-serif font-bold mb-4">
                Ready for Hands-On Help?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                While our free resources are helpful, nothing beats personalized organizing solutions. Get a custom plan tailored to your Michigan home and lifestyle.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleBookingClick}
                  size="lg"
                  className="brand-gradient hover:opacity-90 text-white font-semibold"
                >
                  Book Free Consultation
                </Button>
                <Button 
                  onClick={() => navigate('/blog')}
                  variant="outline" 
                  size="lg"
                >
                  Read Our Blog
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer onBookingClick={handleBookingClick} />
    </>
  );
};

export default Resources;